import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Divider,
  IconButton,
  Grid,
  Autocomplete,
  Menu,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toast } from "react-toastify";

import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import {
  getCashCollectionApi,
  exportCashCollectionApi,
  exportCashCollectionPDF,
  exportCashCollectionSummaryExcel,
  exportCashCollectionSummaryPDF,
} from "../services/reports.service";
import { getUserApi } from "../services/user.service";
import { getTransactionTypeApi } from "../services/transaction-type.service";
import { useGetApi } from "../hooks/useGetApi";
import { DEFAULT_LIMIT, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";
import { reportDayEndISO, reportDayStartISO } from "../utils/reportDateRange";
import TablePagination from "@mui/material/TablePagination";

const TAB = {
  CLEAR: "clear",
  CANCELLED: "cancelled",
  REFUND: "refund",
  TABLE: "table",
};

const emptySummary = () => ({
  cleared: { count: 0, totalAmount: 0 },
  cancelled: { count: 0, totalAmount: 0 },
  refund: { count: 0, totalAmount: 0 },
});

const formatDdMmYyyy = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatInr = (n) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n) || 0);

const visitTypeOptions = [
  { label: "OPD", value: "OPD" },
  { label: "IPD", value: "IPD" },
  { label: "Outsider", value: "Outsider" },
];

const paymentFilterList = [
  { label: "Invoice Payment", value: "InvoicePayment" },
  { label: "Self Payment", value: "SelfPayment" },
  { label: "Refund", value: "Refund" },
  { label: "IPD Advance", value: "IPDAdvance" },
];

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};
const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const menuItems = ["Excel", "PDF", "Report Mail Send Scheduler", "Report Scheduler"];

function tabToCategory(tabId) {
  if (tabId === TAB.CLEAR) return "cleared";
  if (tabId === TAB.CANCELLED) return "cancelled";
  if (tabId === TAB.REFUND) return "refund";
  return "all";
}

export default function ReportsCashCollection() {
  const navigate = useNavigate();

  const {
    dataList: users,
    isLoading: usersLoading,
  } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000, page: 1 },
    dataKey: "users",
  });

  const {
    dataList: transactionList,
    isLoading: transactionTypesLoading,
  } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: {},
  });

  const userOptions = React.useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    return users.map((user) => ({
      label: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Unknown User",
      value: user._id,
    }));
  }, [users]);

  const transactionTypeList = React.useMemo(() => {
    if (!transactionList || !Array.isArray(transactionList)) return [];
    const allTypes = [];
    transactionList.forEach((group) => {
      if (group.transactionTypes && Array.isArray(group.transactionTypes)) {
        group.transactionTypes.forEach((type) => {
          if (type.active !== false) {
            allTypes.push({
              label: `${type.name || ""} (${type.paymentMethod || ""})`.trim(),
              value: type._id,
              _id: type._id,
              name: type.name,
              paymentMethod: type.paymentMethod,
            });
          }
        });
      }
    });
    return allTypes;
  }, [transactionList]);

  const [formData, setFormData] = useState({
    userName: [],
    startDate: null,
    endDate: null,
    transactionTypes: [],
    paymentTypes: [],
    visitType: [],
  });

  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTabId, setActiveTabId] = useState(TAB.TABLE);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    patientUHID: true,
    patientName: true,
    amount: true,
    invoiceNo: true,
    paymentDate: true,
    transactionType: true,
    paymentType: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const prevPageRef = useRef(page);
  const prevRowsPerPageRef = useRef(rowsPerPage);
  const isSearchingRef = useRef(false);

  const visibleTabs = React.useMemo(() => {
    if (!hasSearched || !summary) return [];
    const tabs = [];
    if (summary.cleared.count >= 1) tabs.push(TAB.CLEAR);
    if (summary.cancelled.count >= 1) tabs.push(TAB.CANCELLED);
    if (summary.refund.count >= 1) tabs.push(TAB.REFUND);
    tabs.push(TAB.TABLE);
    return tabs;
  }, [hasSearched, summary]);

  const buildRequestParams = (tabId, currentPage, currentRowsPerPage) => {
    const transactionType = (formData.transactionTypes || [])
      .map((t) => t?.value || t?._id)
      .filter(Boolean)
      .join(",");
    const paymentType = (formData.paymentTypes || []).map((p) => p?.value).filter(Boolean).join(",");

    const params = {
      recordedBy: formData.userName?.map((user) => user.value).join(","),
      startDate: reportDayStartISO(formData.startDate),
      endDate: reportDayEndISO(formData.endDate),
      transactionType: transactionType || undefined,
      paymentType: paymentType || undefined,
      visitType: formData.visitType?.map((item) => item.value).join(","),
      category: tabToCategory(tabId),
      summaryOnly: tabId === TAB.TABLE ? "true" : undefined,
      page: tabId === TAB.TABLE ? 1 : currentPage + 1,
      limit: tabId === TAB.TABLE ? 1 : currentRowsPerPage,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === "" || params[key] === null) {
        delete params[key];
      }
    });
    return params;
  };

  const handleBack = () => navigate(-1);

  const handleTabClick = (tabId) => {
    if (tabId === activeTabId) return;
    setActiveTabId(tabId);
    if (!hasSearched) return;
    setPage(0);
    isSearchingRef.current = true;
    fetchReportData(0, rowsPerPage, tabId);
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const toggleColumn = (columnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "Cash Collection Report",
          text: `Cash Collection Report — ${totalCount} rows`,
          url: window.location.href,
        })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href).then(
        () => toast.success("Report URL copied to clipboard"),
        () => toast.error("Failed to copy URL")
      );
    }
  };

  const netTotal =
    summary != null
      ? Number(summary.cleared?.totalAmount || 0) -
        Number(summary.cancelled?.totalAmount || 0) -
        Number(summary.refund?.totalAmount || 0)
      : 0;

  const fetchReportData = async (
    currentPage = page,
    currentRowsPerPage = rowsPerPage,
    tabId = activeTabId,
    showToast = false
  ) => {
    try {
      setLoading(true);
      const params = buildRequestParams(tabId, currentPage, currentRowsPerPage);
      const response = await getCashCollectionApi(params);

      if (response?.data) {
        const s = response.data.summary || emptySummary();
        setSummary(s);

        const tabsNow = [];
        if (s.cleared.count >= 1) tabsNow.push(TAB.CLEAR);
        if (s.cancelled.count >= 1) tabsNow.push(TAB.CANCELLED);
        if (s.refund.count >= 1) tabsNow.push(TAB.REFUND);
        tabsNow.push(TAB.TABLE);

        let effectiveTab = tabId;
        if (!tabsNow.includes(tabId)) {
          effectiveTab = TAB.TABLE;
          setActiveTabId(TAB.TABLE);
        }

        if (effectiveTab === TAB.TABLE) {
          setReportData([]);
          setTotalCount(0);
        } else {
          let dataArray = [];
          if (response.data.payments && Array.isArray(response.data.payments)) {
            dataArray = response.data.payments;
          } else if (Array.isArray(response.data)) {
            dataArray = response.data;
          }
          setTotalCount(response.data.total || 0);
          setReportData(dataArray);
        }

        if (showToast) toast.success("Report data loaded successfully");
      } else {
        setSummary(emptySummary());
        setReportData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Failed to fetch report data");
      setSummary(emptySummary());
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const errorFields = [];
    if (!formData.userName || formData.userName.length === 0) errorFields.push("userName");
    if (!formData.startDate) errorFields.push("startDate");
    if (!formData.endDate) errorFields.push("endDate");
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    setErrors([]);
    setActiveTabId(TAB.TABLE);
    isSearchingRef.current = true;
    setPage(0);
    setHasSearched(true);
    fetchReportData(0, rowsPerPage, TAB.TABLE, true);
  };

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!hasSearched) {
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return;
    }
    if (isSearchingRef.current) {
      isSearchingRef.current = false;
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return;
    }
    if (activeTabId === TAB.TABLE) {
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return;
    }
    const pageChanged = prevPageRef.current !== page;
    const rowsPerPageChanged = prevRowsPerPageRef.current !== rowsPerPage;
    if (pageChanged || rowsPerPageChanged) {
      fetchReportData(page, rowsPerPage, activeTabId);
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    } else {
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const exportBaseParams = () => {
    const transactionType = (formData.transactionTypes || [])
      .map((t) => t?.value || t?._id)
      .filter(Boolean)
      .join(",");
    const paymentType = (formData.paymentTypes || []).map((p) => p?.value).filter(Boolean).join(",");
    const params = {
      recordedBy: formData.userName?.map((user) => user.value).join(","),
      startDate: reportDayStartISO(formData.startDate),
      endDate: reportDayEndISO(formData.endDate),
      transactionType: transactionType || undefined,
      paymentType: paymentType || undefined,
      visitType: formData.visitType?.map((item) => item.value).join(","),
      category: tabToCategory(activeTabId),
      summaryOnly: activeTabId === TAB.TABLE ? "true" : undefined,
      page: 1,
      limit: 100000,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === "" || params[key] === null) delete params[key];
    });
    return params;
  };

  const handlePDFExport = async () => {
    try {
      setIsExporting(true);
      const exportParams = exportBaseParams();
      const dateRange =
        formData.startDate && formData.endDate
          ? { startDate: reportDayStartISO(formData.startDate), endDate: reportDayEndISO(formData.endDate) }
          : null;

      if (activeTabId === TAB.TABLE) {
        await exportCashCollectionSummaryPDF(exportParams, dateRange);
        toast.success("Summary exported to PDF");
      } else {
        const columns = [
          { header: "SNO", dataKey: "serialNumber" },
          { header: "Patient UHID", dataKey: "patientUHID" },
          { header: "Patient Name", dataKey: "patientName" },
          { header: "Amount", dataKey: "amount" },
          { header: "Invoice No", dataKey: "invoiceNo" },
          { header: "Payment Date", dataKey: "date" },
          { header: "Transaction Type", dataKey: "transactionType" },
          { header: "Payment Type", dataKey: "paymentType" },
        ];
        const result = await exportCashCollectionPDF(exportParams, columns, dateRange);
        if (result.success) toast.success(`Exported ${result.count} records to PDF`);
      }
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error(`PDF export failed: ${error.message || "Unknown error"}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleMenuClick = (item) => {
    handleClose();
    switch (item) {
      case "Excel":
        handleExport();
        break;
      case "PDF":
        handlePDFExport();
        break;
      case "Report Mail Send Scheduler":
        toast.info("Report Mail Send Scheduler feature coming soon");
        break;
      case "Report Scheduler":
        toast.info("Report Scheduler feature coming soon");
        break;
      default:
        break;
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const exportParams = exportBaseParams();
      const columnOrder = [
        "serialNumber",
        "patientUHID",
        "patientName",
        "amount",
        "invoiceNo",
        "date",
        "transactionType",
        "paymentType",
      ];

      if (activeTabId === TAB.TABLE) {
        await exportCashCollectionSummaryExcel(exportParams);
        toast.success("Summary exported to Excel");
      } else {
        const result = await exportCashCollectionApi(exportParams, columnOrder);
        if (result.success) toast.success(`Exported ${result.count} records to Excel`);
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Export failed: ${error.message || "Unknown error"}`);
    } finally {
      setIsExporting(false);
    }
  };

  const renderDataTable = () => {
    const colCount = Object.values(visibleColumns).filter((v) => v).length;
    return (
      <Paper sx={{ mt: 2 }}>
        <Table aria-label="cash collection detail" sx={{ minWidth: 800 }}>
          <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
            <TableRow>
              {visibleColumns.sno && <TableCell>SNO</TableCell>}
              {visibleColumns.patientUHID && <TableCell>Patient UHID</TableCell>}
              {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
              {visibleColumns.amount && <TableCell>Amount</TableCell>}
              {visibleColumns.invoiceNo && <TableCell>Invoice No</TableCell>}
              {visibleColumns.paymentDate && <TableCell>Payment Date</TableCell>}
              {visibleColumns.transactionType && <TableCell>Transaction Type</TableCell>}
              {visibleColumns.paymentType && <TableCell>Payment Type</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(reportData) || reportData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="textSecondary">No data for this tab.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reportData.map((row, index) => (
                <TableRow key={row?._id || index}>
                  {visibleColumns.sno && <TableCell>{page * rowsPerPage + index + 1}</TableCell>}
                  {visibleColumns.patientUHID && <TableCell>{row?.patientUHID || "-"}</TableCell>}
                  {visibleColumns.patientName && <TableCell>{row?.patientName || "-"}</TableCell>}
                  {visibleColumns.amount && <TableCell>₹{formatInr(row?.amount)}</TableCell>}
                  {visibleColumns.invoiceNo && <TableCell>{row?.invoiceNo || "-"}</TableCell>}
                  {visibleColumns.paymentDate && <TableCell>{formatDdMmYyyy(row?.date)}</TableCell>}
                  {visibleColumns.transactionType && <TableCell>{row?.transactionType || "-"}</TableCell>}
                  {visibleColumns.paymentType && <TableCell>{row?.paymentType || "-"}</TableCell>}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalCount > 0 && (
          <TablePagination
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </Paper>
    );
  };

  const renderTableSummaryTab = () => {
    if (!summary) return null;
    const rows = [
      { key: "cleared", label: "Cleared", s: summary.cleared },
      { key: "cancelled", label: "Cancelled", s: summary.cancelled },
      { key: "refund", label: "Refund", s: summary.refund },
    ].filter((r) => r.s.count > 0);

    return (
      <Paper sx={{ mt: 2, p: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 2 }}>Summary</Typography>
        <Box sx={{ fontFamily: "Open Sans", fontSize: "15px", lineHeight: 2, maxWidth: 420 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>Total Cleared:</span>
            <span>{formatInr(summary.cleared.totalAmount)}</span>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>− Total Cancelled:</span>
            <span>{formatInr(summary.cancelled.totalAmount)}</span>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>− Total Refund:</span>
            <span>{formatInr(summary.refund.totalAmount)}</span>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
            <span>Net Total:</span>
            <span>{formatInr(netTotal)}</span>
          </Box>
        </Box>

        {rows.length > 0 && (
          <Table size="small" sx={{ mt: 3, maxWidth: 560 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Total amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">{r.s.count}</TableCell>
                  <TableCell align="right">₹{formatInr(r.s.totalAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    );
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography
              sx={{
                fontFamily: "Open Sans",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "29px",
                color: "#5D6A82",
                marginBottom: "1rem",
              }}
            >
              Collection Report
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", my: "20px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={userOptions || []}
                  multiple
                  limitTags={2}
                  value={formData.userName}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, userName: newValue }));
                    if (errors.includes("userName")) setErrors(errors.filter((e) => e !== "userName"));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  loading={usersLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User Name"
                      name="userName"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                      }}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: errors?.includes("userName") ? "1px solid red" : "1px solid inherit",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      format="DD-MM-YYYY"
                      value={formData.startDate}
                      onChange={(newValue) => {
                        handleDateChange("startDate", newValue);
                        if (errors.includes("startDate")) setErrors(errors.filter((e) => e !== "startDate"));
                      }}
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { "& .MuiFormLabel-asterisk": { color: "red" } } },
                        },
                      }}
                      sx={{
                        bgcolor: "white",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: errors?.includes("startDate") ? "1px solid red" : "1px solid inherit",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date"
                      format="DD-MM-YYYY"
                      value={formData.endDate}
                      onChange={(newValue) => {
                        handleDateChange("endDate", newValue);
                        if (errors.includes("endDate")) setErrors(errors.filter((e) => e !== "endDate"));
                      }}
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { "& .MuiFormLabel-asterisk": { color: "red" } } },
                        },
                      }}
                      sx={{
                        bgcolor: "white",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: errors?.includes("endDate") ? "1px solid red" : "1px solid inherit",
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={transactionTypeList || []}
                  multiple
                  limitTags={2}
                  value={formData.transactionTypes}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, transactionTypes: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  loading={transactionTypesLoading}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    if (option._id && value._id) return String(option._id) === String(value._id);
                    if (option.value && value.value) return String(option.value) === String(value.value);
                    return option.label === value.label;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Transaction Type" name="transactionTypes" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={paymentFilterList || []}
                  multiple
                  limitTags={2}
                  value={formData.paymentTypes}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, paymentTypes: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  renderInput={(params) => <TextField {...params} label="Payment" name="paymentTypes" fullWidth />}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={visitTypeOptions || []}
                  multiple
                  limitTags={2}
                  value={formData.visitType}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, visitType: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => <TextField {...params} label="Visit Type" name="visitType" fullWidth />}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px", gap: 2 }}>
            <Button variant="contained" color="secondary" onClick={handleExport} disabled={isExporting || !hasSearched} sx={{ minWidth: 120 }}>
              {isExporting ? <CircularProgress size={20} /> : "Export"}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{ textTransform: "none", backgroundColor: "rgba(1, 121, 176, 1)", width: "150px" }}
            >
              {loading ? <CircularProgress size={20} /> : "Search"}
            </Button>
          </Box>

          {hasSearched && summary && (
            <Box
              sx={{
                border: "1px solid rgba(223, 232, 239, 1)",
                borderRadius: "8px",
                mb: "30px",
                mt: "20px",
                p: "20px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box
                  sx={{
                    border: "1px solid grey",
                    borderRadius: "5px",
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    cursor: "pointer",
                    mb: "20px",
                  }}
                >
                  {visibleTabs.includes(TAB.CLEAR) && (
                    <>
                      <Typography
                        onClick={() => handleTabClick(TAB.CLEAR)}
                        sx={{
                          color: "grey",
                          ...(activeTabId === TAB.CLEAR && {
                            color: "primary.main",
                            fontWeight: 600,
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                          }),
                        }}
                      >
                        Clear - Advance Patient Payment
                      </Typography>
                      <Divider flexItem orientation="vertical" sx={{ height: 24 }} />
                    </>
                  )}
                  {visibleTabs.includes(TAB.CANCELLED) && (
                    <>
                      <Typography
                        onClick={() => handleTabClick(TAB.CANCELLED)}
                        sx={{
                          color: "grey",
                          ...(activeTabId === TAB.CANCELLED && {
                            color: "primary.main",
                            fontWeight: 600,
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                          }),
                        }}
                      >
                        Cancelled - Advance Patient Payment
                      </Typography>
                      <Divider flexItem orientation="vertical" sx={{ height: 24 }} />
                    </>
                  )}
                  {visibleTabs.includes(TAB.REFUND) && (
                    <>
                      <Typography
                        onClick={() => handleTabClick(TAB.REFUND)}
                        sx={{
                          color: "grey",
                          ...(activeTabId === TAB.REFUND && {
                            color: "primary.main",
                            fontWeight: 600,
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                          }),
                        }}
                      >
                        Refund
                      </Typography>
                      <Divider flexItem orientation="vertical" sx={{ height: 24 }} />
                    </>
                  )}
                  <Typography
                    onClick={() => handleTabClick(TAB.TABLE)}
                    sx={{
                      color: "grey",
                      ...(activeTabId === TAB.TABLE && {
                        color: "primary.main",
                        fontWeight: 600,
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                      }),
                    }}
                  >
                    Table
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: "20px" }}>
                  <Typography sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}>
                    Rows: {activeTabId === TAB.TABLE ? "—" : totalCount || 0} | Columns:{" "}
                    {Object.values(visibleColumns).filter((v) => v).length}
                  </Typography>
                  {activeTabId !== TAB.TABLE && (
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon sx={{ cursor: "pointer", color: "transparent", fontSize: "18px" }} />
                    </Box>
                  )}
                  <Box sx={iconStyleProps} onClick={handleShare}>
                    <ShareIcon sx={{ cursor: "pointer", fontSize: "18px" }} />
                  </Box>
                  <Box sx={iconStyleProps}>
                    <IconButton onClick={handleClick}>
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {activeTabId === TAB.TABLE ? renderTableSummaryTab() : renderDataTable()}

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle} disabled={!hasSearched}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>

              {activeTabId !== TAB.TABLE && (
                <Menu
                  anchorEl={filterAnchorEl}
                  open={filterMenuOpen}
                  onClose={handleFilterClose}
                  PaperProps={{
                    sx: {
                      borderRadius: "12px",
                      boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                      padding: "8px 0",
                      width: "200px",
                    },
                  }}
                >
                  <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                    Show/Hide Columns
                  </MenuItem>
                  <Divider />
                  {[
                    ["sno", "SNO"],
                    ["patientUHID", "Patient UHID"],
                    ["patientName", "Patient Name"],
                    ["amount", "Amount"],
                    ["invoiceNo", "Invoice No"],
                    ["paymentDate", "Payment Date"],
                    ["transactionType", "Transaction Type"],
                    ["paymentType", "Payment Type"],
                  ].map(([key, label]) => (
                    <MenuItem key={key} onClick={() => toggleColumn(key)} sx={menuItemStyle}>
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Box sx={{ flex: 1 }}>{label}</Box>
                        {visibleColumns[key] && <Box sx={{ color: "primary.main" }}>✓</Box>}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}
